import { useTranslation } from 'react-i18next';
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Heart, 
  User as UserIcon, 
  Phone, 
  Activity, 
  ShieldCheck, 
  ArrowRight, 
  ArrowRight as ArrowLeft, // Récupération de la bonne icône, mais c'était importé sous "ArrowLeft" pour l'affichage
  ArrowLeft as RealArrowLeft,
  CheckCircle2,
  Stethoscope,
  MapPin,
  AlertCircle,
  Video,
  Mic,
  Camera
} from 'lucide-react';
import { UserRole } from '../../types/User';
import type { User } from '../../types/User';
import { useNavigate } from 'react-router-dom';
import { logger } from '../../utils/logger';
import {
  africanCountries,
  getRegionsByCountry,
  getCitiesByRegion
} from '../../constants/geography';

interface HealthOnboardingProps {
  user: User;
  onComplete: (data: Partial<User>) => Promise<void>;
}

const HealthOnboarding: React.FC<HealthOnboardingProps> = ({ user, onComplete }) => {
  const { t } = useTranslation('common');
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    dateOfBirth: '',
    gender: '',
    country: 'CM',
    region: '',
    city: '',
    phoneNumber: '',
    emergencyContact: {
      name: '',
      phone: '',
      relation: ''
    },
    bloodGroup: '',
    allergies: '',
    chronicDiseases: '',
    specialty: '', // For Doctors
    licenseNumber: '', // For Doctors
  });
  
  const [loading, setLoading] = useState(false);
  const [hardwareTested, setHardwareTested] = useState(false);
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  
  const navigate = useNavigate();

  const totalSteps = user.role === UserRole.DOCTOR ? 4 : 5;

  const nextStep = () => {
    stopHardware();
    setStep(s => Math.min(s + 1, totalSteps + 1));
  };
  
  const prevStep = () => {
    stopHardware();
    setStep(s => Math.max(s - 1, 1));
  };

  useEffect(() => {
    if (mediaStream && videoRef.current) {
      videoRef.current.srcObject = mediaStream;
    }
  }, [mediaStream]);

  useEffect(() => {
    return () => stopHardware();
  }, []);

  const availableRegions = formData.country ? getRegionsByCountry(formData.country) : [];
  const availableCities = formData.region ? getCitiesByRegion(formData.country, formData.region) : [];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...(prev as Record<string, unknown>)[parent] as Record<string, unknown>,
          [child]: value
        }
      }));
    } else {
      setFormData(prev => {
        const newData = { ...prev, [name]: value };
        // Reset dependent fields when parent changes
        if (name === 'country') {
          newData.region = '';
          newData.city = '';
        } else if (name === 'region') {
          newData.city = '';
        }
        return newData;
      });
    }
  };

  const testHardware = async () => {
    try {
      setLoading(true);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
      setMediaStream(stream);
      setHardwareTested(true);
    } catch (error) {
      logger.error('Erreur accès média:', error);
      alert(t('devicePreflight.errors.notAllowed'));
    } finally {
      setLoading(false);
    }
  };

  const stopHardware = () => {
    if (mediaStream) {
      mediaStream.getTracks().forEach(track => track.stop());
      setMediaStream(null);
    }
  };

  const handleFinish = async () => {
    setLoading(true);
    stopHardware();
    try {
      await onComplete(formData);
    } catch (error) {
      logger.error('Error completing onboarding', error);
      setLoading(false); // Only unset loading on error, on success it unmounts
    }
  };

  const stepVariants = {
    initial: { opacity: 0, x: 20 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -20 }
  };

  const renderWelcomeText = () => {
    if (user.role === UserRole.DOCTOR) {
      return (
        <>
          <h2 className="text-3xl font-bold text-white tracking-tight">
            {t('healthOnboarding.welcomeDoctor')}<span className="text-blue-400">{user.name?.startsWith('Dr.') ? user.name : `Dr. ${user.name}`}</span>
          </h2>
          <p className="text-slate-400 text-lg font-light leading-relaxed">
            {t('healthOnboarding.welcomeDoctorDesc')}
          </p>
        </>
      );
    }
    return (
      <>
        <h2 className="text-3xl font-bold text-white tracking-tight">
          {t('healthOnboarding.welcomePatient')}<span className="text-blue-400">{user.name}</span>
        </h2>
        <p className="text-slate-400 text-lg font-light leading-relaxed">
          {t('healthOnboarding.welcomePatientDesc')}
        </p>
      </>
    );
  };

  const isHardwareStep = (step === totalSteps);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/90 backdrop-blur-xl p-4 sm:p-6 overflow-y-auto font-outfit text-slate-100">
      <div className="w-full max-w-2xl bg-slate-900 border border-slate-800 rounded-[2rem] shadow-2xl shadow-blue-500/10 overflow-hidden relative my-auto">
        {/* Header Progress */}
        <div className="absolute top-0 left-0 w-full h-1.5 bg-slate-800">
          <motion.div 
            className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 shadow-[0_0_10px_rgba(59,130,246,0.5)]"
            initial={{ width: 0 }}
            animate={{ width: `${(step / (totalSteps + 1)) * 100}%` }}
          />
        </div>

        <div className="p-8 sm:p-12">
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div key="step1" {...stepVariants} className="space-y-6">
                <div className="w-16 h-16 bg-blue-500/20 rounded-2xl flex items-center justify-center mb-8">
                  {user.role === UserRole.DOCTOR ? (
                    <Stethoscope className="w-8 h-8 text-blue-500 animate-pulse" />
                  ) : (
                    <Heart className="w-8 h-8 text-blue-500 animate-pulse" />
                  )}
                </div>
                {renderWelcomeText()}
                <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700/50 flex items-start space-x-3">
                  <ShieldCheck className="w-5 h-5 text-emerald-500 mt-1 shrink-0" />
                  <p className="text-sm text-slate-300">
                    {t('healthOnboarding.hdsNotice')}
                  </p>
                </div>
                <button 
                  onClick={nextStep}
                  className="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl font-bold transition-all shadow-lg flex items-center justify-center group"
                >
                  {t('healthOnboarding.letsGo')}
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div key="step2" {...stepVariants} className="space-y-6">
                <div className="flex items-center space-x-2 text-blue-400 font-bold text-sm uppercase tracking-wider">
                  {user.role === UserRole.DOCTOR ? <Stethoscope className="w-4 h-4" /> : <UserIcon className="w-4 h-4" />}
                  <span>{t('waitingRoom.position')} 1 : {user.role === UserRole.DOCTOR ? t('healthOnboarding.step1DoctorTitle') : t('healthOnboarding.step1PatientTitle')}</span>
                </div>
                <h3 className="text-2xl font-bold text-white">
                  {user.role === UserRole.DOCTOR ? t('healthOnboarding.step1DoctorSubtitle') : t('healthOnboarding.step1PatientSubtitle')}
                </h3>
                
                {user.role === UserRole.DOCTOR ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm text-slate-400 ml-1">{t('healthOnboarding.specialty')}</label>
                      <input 
                        type="text" 
                        name="specialty"
                        placeholder={t('healthOnboarding.specialtyPlaceholder')}
                        value={formData.specialty}
                        onChange={handleInputChange}
                        className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm text-slate-400 ml-1">{t('healthOnboarding.licenseNumber')}</label>
                      <input 
                        type="text" 
                        name="licenseNumber"
                        placeholder={t('healthOnboarding.licenseNumberPlaceholder')}
                        value={formData.licenseNumber}
                        onChange={handleInputChange}
                        className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm text-slate-400 ml-1">{t('healthOnboarding.birthDate')}</label>
                      <input 
                        type="date" 
                        name="dateOfBirth"
                        value={formData.dateOfBirth}
                        onChange={handleInputChange}
                        className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm text-slate-400 ml-1">{t('healthOnboarding.gender')}</label>
                      <select 
                        name="gender"
                        value={formData.gender}
                        onChange={handleInputChange}
                        className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                      >
                        <option value="">{t('healthOnboarding.select')}</option>
                        <option value="male">{t('healthOnboarding.genderMale')}</option>
                        <option value="female">{t('healthOnboarding.genderFemale')}</option>
                        <option value="other">{t('healthOnboarding.genderOther')}</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* Localisation complète (Pays, Région, Ville) */}
                <div className="bg-slate-800/30 p-5 rounded-2xl border border-slate-700 space-y-4 mt-6">
                  <p className="text-sm font-bold text-slate-300 uppercase tracking-tighter">{t('healthOnboarding.yourLocation')}</p>
                  
                  <div className="space-y-2">
                    <label className="text-sm text-slate-400 ml-1">{t('healthOnboarding.country')}</label>
                    <div className="relative">
                      <MapPin className="absolute left-4 top-3.5 w-5 h-5 text-slate-500" />
                      <select
                        name="country"
                        value={formData.country}
                        onChange={handleInputChange}
                        className="w-full bg-slate-800 border border-slate-700 rounded-xl pl-12 pr-4 py-3 text-white focus:ring-2 focus:ring-blue-500 outline-none appearance-none"
                      >
                        <option value="">{t('healthOnboarding.selectCountry')}</option>
                        {africanCountries.map(country => (
                          <option key={country.code} value={country.code}>
                            {country.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm text-slate-400 ml-1">{t('healthOnboarding.region')}</label>
                      <select
                        name="region"
                        value={formData.region}
                        onChange={handleInputChange}
                        disabled={!formData.country}
                        className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 outline-none appearance-none disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <option value="">{t('healthOnboarding.select')}</option>
                        {availableRegions.map(region => (
                          <option key={region.name} value={region.name}>{region.name}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="space-y-2">
                      <label className="text-sm text-slate-400 ml-1">{t('healthOnboarding.city')}</label>
                      <select
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        disabled={!formData.region}
                        className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 outline-none appearance-none disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <option value="">{t('healthOnboarding.select')}</option>
                        {availableCities.map(c => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-6">
                  <button onClick={prevStep} className="text-slate-400 hover:text-white font-medium flex items-center">
                    <RealArrowLeft className="mr-2 w-4 h-4" /> {t('healthOnboarding.back')}
                  </button>
                  <button 
                    onClick={nextStep}
                    disabled={user.role === UserRole.DOCTOR ? (!formData.specialty || !formData.licenseNumber || !formData.city) : (!formData.dateOfBirth || !formData.city)}
                    className="px-8 py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl font-bold transition-all shadow-lg"
                  >
                    {t('healthOnboarding.next')}
                  </button>
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div key="step3" {...stepVariants} className="space-y-6">
                <div className="flex items-center space-x-2 text-blue-400 font-bold text-sm uppercase tracking-wider">
                  <Phone className="w-4 h-4" />
                  <span>{t('waitingRoom.position')} 2 : {t('healthOnboarding.step2Title')}</span>
                </div>
                <h3 className="text-2xl font-bold text-white">
                  {user.role === UserRole.DOCTOR ? t('healthOnboarding.step2DoctorSubtitle') : t('healthOnboarding.step2PatientSubtitle')}
                </h3>
                
                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm text-slate-400 ml-1">{t('healthOnboarding.phoneLabel')}</label>
                    <input 
                      type="tel" 
                      name="phoneNumber"
                      placeholder={t('healthOnboarding.phonePlaceholder')}
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                      className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                  </div>

                  <div className="p-5 bg-slate-800/30 rounded-2xl border border-slate-700 space-y-4">
                    <p className="text-sm font-bold text-slate-300 uppercase tracking-tighter">{t('healthOnboarding.iceTitle')}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <input 
                        type="text" 
                        name="emergencyContact.name"
                        placeholder={t('healthOnboarding.iceNamePlaceholder')}
                        value={formData.emergencyContact.name}
                        onChange={handleInputChange}
                        className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                      />
                      <input 
                        type="tel" 
                        name="emergencyContact.phone"
                        placeholder={t('healthOnboarding.icePhonePlaceholder')}
                        value={formData.emergencyContact.phone}
                        onChange={handleInputChange}
                        className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                      />
                    </div>
                    <select 
                      name="emergencyContact.relation"
                      value={formData.emergencyContact.relation}
                      onChange={handleInputChange}
                      className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                    >
                      <option value="">{t('healthOnboarding.iceRelationPlaceholder')}</option>
                      <option value="parent">{t('patientSettings.emergency.relations.parent')}</option>
                      <option value="conjoint">{t('patientSettings.emergency.relations.spouse')}</option>
                      <option value="enfant">{t('patientSettings.emergency.relations.child')}</option>
                      <option value="frere_soeur">{t('patientSettings.emergency.relations.sibling')}</option>
                      <option value="ami">{t('patientSettings.emergency.relations.friend')}</option>
                      <option value="autre">{t('patientSettings.emergency.relations.other')}</option>
                    </select>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-6">
                  <button onClick={prevStep} className="text-slate-400 hover:text-white font-medium flex items-center">
                    <RealArrowLeft className="mr-2 w-4 h-4" /> {t('healthOnboarding.back')}
                  </button>
                  <button 
                    onClick={nextStep}
                    disabled={!formData.phoneNumber || !formData.emergencyContact.name}
                    className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold transition-all shadow-lg disabled:opacity-50"
                  >
                    {t('healthOnboarding.next')}
                  </button>
                </div>
              </motion.div>
            )}

            {step === 4 && user.role === UserRole.PATIENT && (
              <motion.div key="step4" {...stepVariants} className="space-y-6">
                <div className="flex items-center space-x-2 text-blue-400 font-bold text-sm uppercase tracking-wider">
                  <Activity className="w-4 h-4" />
                  <span>{t('waitingRoom.position')} 3 : {t('healthOnboarding.step3Title')}</span>
                </div>
                <h3 className="text-2xl font-bold text-white">{t('healthOnboarding.step3PatientSubtitle')}</h3>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm text-slate-400 ml-1">{t('healthOnboarding.bloodGroup')}</label>
                      <select 
                        name="bloodGroup"
                        value={formData.bloodGroup}
                        onChange={handleInputChange}
                        className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                      >
                        <option value="">{t('healthOnboarding.bloodGroupUnknown')}</option>
                        <option value="A+">A+</option>
                        <option value="A-">A-</option>
                        <option value="B+">B+</option>
                        <option value="B-">B-</option>
                        <option value="O+">O+</option>
                        <option value="O-">O-</option>
                        <option value="AB+">AB+</option>
                        <option value="AB-">AB-</option>
                      </select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm text-slate-400 ml-1">{t('healthOnboarding.allergies')}</label>
                    <textarea 
                      name="allergies"
                      placeholder={t('healthOnboarding.allergiesPlaceholder')}
                      value={formData.allergies}
                      onChange={handleInputChange}
                      className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 outline-none h-24 resize-none"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between pt-6">
                  <button onClick={prevStep} className="text-slate-400 hover:text-white font-medium flex items-center">
                    <RealArrowLeft className="mr-2 w-4 h-4" /> {t('healthOnboarding.back')}
                  </button>
                  <button 
                    onClick={nextStep}
                    className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold transition-all shadow-lg"
                  >
                    {t('healthOnboarding.next')}
                  </button>
                </div>
              </motion.div>
            )}

            {isHardwareStep && (
              <motion.div key="hardware" {...stepVariants} className="space-y-6">
                <div className="flex items-center space-x-2 text-blue-400 font-bold text-sm uppercase tracking-wider">
                  <Video className="w-4 h-4" />
                  <span>{t('healthOnboarding.stepHardwareTitle')}</span>
                </div>
                <h3 className="text-2xl font-bold text-white">{t('healthOnboarding.stepHardwareSubtitle')}</h3>
                <p className="text-slate-400">{t('healthOnboarding.stepHardwareDesc')}</p>

                <div className="bg-slate-800/30 p-6 rounded-2xl border border-slate-700 text-center space-y-6">
                  {mediaStream ? (
                    <div className="relative w-full max-w-sm mx-auto aspect-video rounded-xl overflow-hidden bg-black border border-slate-600 shadow-lg shadow-blue-500/20">
                      <video 
                        ref={videoRef} 
                        autoPlay 
                        playsInline 
                        muted 
                        className="w-full h-full object-cover transform -scale-x-100" 
                      />
                      <div className="absolute bottom-3 left-0 w-full flex justify-center space-x-4">
                        <div className="bg-emerald-500/90 p-2 rounded-full backdrop-blur-sm"><Mic className="w-4 h-4 text-white" /></div>
                        <div className="bg-emerald-500/90 p-2 rounded-full backdrop-blur-sm"><Camera className="w-4 h-4 text-white" /></div>
                      </div>
                    </div>
                  ) : (
                    <div className="py-8">
                      <div className="w-20 h-20 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4 border border-slate-700">
                        <Video className="w-8 h-8 text-slate-500" />
                      </div>
                      <button 
                        onClick={testHardware}
                        disabled={loading}
                        className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl font-medium transition-all"
                      >
                        {loading ? t('healthOnboarding.hardwareConnecting') : t('healthOnboarding.hardwareConnectBtn')}
                      </button>
                    </div>
                  )}
                  
                  {hardwareTested && (
                    <div className="flex items-center justify-center space-x-2 text-emerald-400 text-sm font-bold">
                      <CheckCircle2 className="w-5 h-5" />
                      <span>{t('healthOnboarding.hardwareSuccess')}</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between pt-6">
                  <button onClick={prevStep} className="text-slate-400 hover:text-white font-medium flex items-center">
                    <RealArrowLeft className="mr-2 w-4 h-4" /> {t('healthOnboarding.back')}
                  </button>
                  <button 
                    onClick={handleFinish}
                    disabled={!hardwareTested || loading}
                    className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold transition-all shadow-lg disabled:opacity-50"
                  >
                    {loading ? t('healthOnboarding.hardwareFinishing') : t('healthOnboarding.hardwareFinishBtn')}
                  </button>
                </div>
              </motion.div>
            )}

          </AnimatePresence>
        </div>

        {/* Footer info */}
        {step <= totalSteps && (
          <div className="px-8 py-4 bg-slate-800/50 border-t border-slate-800 flex items-center justify-center space-x-2 text-xs text-slate-500">
            <ShieldCheck className="w-3 h-3" />
            <span>{t('healthOnboarding.hdsFooter')}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default HealthOnboarding;
